import { useState } from 'react';

export interface RouteInputs {
  origin: string;
  via: string[];
  destination: string;
}

export interface RoutePlannerLabels {
  origin: string;
  destination: string;
  via: string;
  optional: string;
  addVia: string;
  removeVia: string;
  calculate: string;
  calculating: string;
}

export interface RoutePlannerProps {
  inputs: RouteInputs;
  calculating: boolean;
  labels: RoutePlannerLabels;
  onInputsChange: (inputs: RouteInputs) => void;
  onCalculate: () => Promise<void>;
}

export function RoutePlanner({ inputs, calculating, labels, onInputsChange, onCalculate }: RoutePlannerProps) {
  const [error, setError] = useState('');
  const update = (next: Partial<RouteInputs>) => onInputsChange({ ...inputs, ...next });

  const calculate = async () => {
    setError('');
    try {
      await onCalculate();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    }
  };

  return (
    <form className="route-planner" onSubmit={(event) => { event.preventDefault(); void calculate(); }}>
      <label>
        <span>{labels.origin}</span>
        <input
          value={inputs.origin}
          onChange={(event) => update({ origin: event.target.value })}
          aria-label={labels.origin}
          autoComplete="off"
        />
      </label>
      <div className="route-planner__vias">
        {inputs.via.map((via, index) => (
          <div className="route-planner__via" key={index}>
            <label>
              <span>{labels.via} {index + 1} <small>{labels.optional}</small></span>
              <input
                value={via}
                onChange={(event) => update({ via: inputs.via.map((value, viaIndex) => viaIndex === index ? event.target.value : value) })}
                aria-label={`${labels.via} ${index + 1}`}
                autoComplete="off"
              />
            </label>
            <button
              type="button"
              onClick={() => update({ via: inputs.via.filter((_, viaIndex) => viaIndex !== index) })}
              aria-label={`${labels.removeVia} ${index + 1}`}
            >
              {labels.removeVia}
            </button>
          </div>
        ))}
      </div>
      <button type="button" className="route-planner__add" onClick={() => update({ via: [...inputs.via, ''] })}>{labels.addVia}</button>
      <label>
        <span>{labels.destination}</span>
        <input
          value={inputs.destination}
          onChange={(event) => update({ destination: event.target.value })}
          aria-label={labels.destination}
          autoComplete="off"
        />
      </label>
      {error && <p className="route-planner__error" role="alert">{error}</p>}
      <button type="submit" disabled={calculating}>{calculating ? labels.calculating : labels.calculate}</button>
    </form>
  );
}
